import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { mangaList } from '@/mocks/manga';

const categoryMap: Record<string, string> = {
  romance: '恋爱',
  action: '动作',
  fantasy: '奇幻',
  drama: '剧情',
  thriller: '悬疑',
  comedy: '喜剧',
  horror: '恐怖',
  mature: '都市',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const manga = useMemo(() => mangaList.find((m) => m.id === id), [id]);

  const relatedManga = useMemo(() => {
    if (!manga) return [];
    return mangaList
      .filter((m) => m.category === manga.category && m.id !== manga.id)
      .slice(0, 6);
  }, [manga]);

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <SEOMeta
          title="漫画未找到 - 韩漫漫画网"
          description="抱歉，该漫画不存在或已被移除。请浏览韩漫漫画网其他韩国漫画资源。"
        />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-800 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，该漫画不存在或已被移除</p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all text-sm font-medium"
            >
              <i className="ri-arrow-left-line"></i>返回分类浏览
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = categoryMap[manga.category] || manga.category;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: manga.title,
    author: { '@type': 'Person', name: manga.author },
    description: manga.description,
    url: `https://hanmanmh.com/article/${manga.id}`,
    image: manga.cover,
    inLanguage: 'zh-CN',
    genre: categoryLabel,
    datePublished: manga.updateDate,
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${manga.title} - 韩漫漫画网 | 韩漫在线阅读`}
        description={`${manga.title} - ${manga.author}作品。${manga.description.substring(0, 120)}韩漫漫画网免费提供完整版韩国漫画在线阅读，无删减下拉式阅读。`}
        keywords={`${manga.title},${categoryLabel}韩漫,韩漫漫画网,韩漫在线阅读,免费韩国漫画`}
        canonicalUrl={`https://hanmanmh.com/article/${manga.id}`}
        ogTitle={`${manga.title} - 韩漫漫画网`}
        ogDescription={manga.description}
        ogImage={manga.cover}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${categoryLabel}韩漫`, href: `/category/${manga.category}` },
              { label: manga.title },
            ]}
          />
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <article className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
              <div className="rounded-lg overflow-hidden aspect-[3/4]">
                <img
                  src={manga.cover}
                  alt={manga.title}
                  title={`${manga.title} - 韩漫漫画网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                <strong>{manga.title}</strong>
              </h1>
              <p className="text-sm text-foreground-600 mb-4">
                作者：{manga.author}
              </p>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {manga.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
                <span
                  className={`px-3 py-1 text-xs rounded-full font-medium whitespace-nowrap ${
                    manga.status === '连载中'
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'bg-primary-100 text-primary-700'
                  }`}
                >
                  {manga.status}
                </span>
              </div>

              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-secondary-500"></i>
                  <span className="text-sm font-semibold text-foreground-800">{manga.rating}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <div className="flex items-center gap-1">
                  <i className="ri-eye-line text-foreground-500"></i>
                  <span className="text-sm text-foreground-600">
                    {manga.views >= 10000 ? `${(manga.views / 10000).toFixed(0)}万` : manga.views} 次阅读
                  </span>
                </div>
                <span className="text-foreground-300">|</span>
                <div className="flex items-center gap-1">
                  <i className="ri-file-list-3-line text-foreground-500"></i>
                  <span className="text-sm text-foreground-600">{manga.chapters.length} 话</span>
                </div>
                <span className="text-foreground-300">|</span>
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line text-foreground-500"></i>
                  <span className="text-sm text-foreground-600">更新于 {manga.updateDate}</span>
                </div>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {manga.description}
              </p>

              <Link
                to={`/article/${manga.id}`}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-play-circle-line"></i>开始阅读
              </Link>
            </div>
          </article>

          <section className="mt-10">
            <h2 className="text-xl font-bold text-foreground-900 font-heading mb-4">
              章节列表
            </h2>
            <div className="border border-background-200 rounded-xl divide-y divide-background-200 overflow-hidden">
              {manga.chapters.map((chapter) => (
                <div
                  key={chapter.number}
                  className="flex items-center justify-between px-5 py-3.5 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-background-200 rounded-full text-xs font-medium text-foreground-600">
                      {chapter.number}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{chapter.title}</p>
                      <p className="text-xs text-foreground-400">{chapter.updateDate}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-primary-600 text-sm">
                    <i className="ri-eye-line"></i>
                    <span>阅读</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {relatedManga.length > 0 && (
            <section className="mt-12">
              <h2 className="text-xl font-bold text-foreground-900 font-heading mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4" data-product-shop>
                {relatedManga.map((rm) => (
                  <Link
                    key={rm.id}
                    to={`/article/${rm.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                      <img
                        src={rm.cover}
                        alt={rm.title}
                        title={`${rm.title} - 韩漫漫画网`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-xs font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rm.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}